import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarEvent, CalendarView, EventParticipant } from '../types/calendar';
import { useNavigate } from 'react-router-dom';

const Calendar = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<CalendarView>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setEvents(dummyEvents);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Navigation functions
  const goToToday = () => setCurrentDate(new Date());

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // Calendar view header
  const getViewTitle = () => {
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      year: 'numeric'
    };

    if (view === 'day') {
      options.day = 'numeric';
    }

    return currentDate.toLocaleDateString('en-US', options);
  };

  // Helper function to get days in month view
  const getDaysForMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

    const days = [];

    // Add days from previous month to fill the first row
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
        isToday: false
      });
    }

    // Add days of current month
    const today = new Date();
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        isCurrentMonth: true,
        isToday:
          date.getDate() === today.getDate() &&
          date.getMonth() === today.getMonth() &&
          date.getFullYear() === today.getFullYear()
      });
    }

    // Add days from next month to complete the grid (6 weeks)
    const remainingDays = 42 - days.length; // 6 rows * 7 days = 42
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        isToday: false
      });
    }

    return days;
  };

  // Get events for a specific day
  const getEventsForDay = (date: Date) => {
    return events.filter(event => {
      const eventStartDate = new Date(event.startDate);
      const eventEndDate = new Date(event.endDate);

      // Check if the event falls on this day
      return (
        (eventStartDate.getFullYear() === date.getFullYear() &&
         eventStartDate.getMonth() === date.getMonth() &&
         eventStartDate.getDate() === date.getDate()) ||
        (eventEndDate.getFullYear() === date.getFullYear() &&
         eventEndDate.getMonth() === date.getMonth() &&
         eventEndDate.getDate() === date.getDate()) ||
        (event.isAllDay &&
         date >= new Date(eventStartDate.setHours(0,0,0,0)) &&
         date <= new Date(eventEndDate.setHours(23,59,59,999)))
      );
    });
  };

  // Placeholder render functions for different views
  const renderDayView = () => (
    <div className="h-full">Day view placeholder</div>
  );

  const renderWeekView = () => (
    <div className="h-full">Week view placeholder</div>
  );

  const renderMonthView = () => {
    const days = getDaysForMonthView();
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="h-full flex flex-col">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-gray-700">
          {weekdays.map(day => (
            <div key={day} className="py-2 text-center text-sm font-medium text-gray-400">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="flex-1 grid grid-cols-7 grid-rows-6 gap-[1px] bg-gray-700">
          {days.map((day, index) => {
            const dayEvents = getEventsForDay(day.date);
            return (
              <div
                key={index}
                className={`bg-tertiary p-1 min-h-[100px] flex flex-col ${
                  day.isCurrentMonth ? 'opacity-100' : 'opacity-40'
                }`}
              >
                <div className={`text-right p-1 ${
                  day.isToday ? 'bg-accent text-white rounded-full w-7 h-7 flex items-center justify-center ml-auto' : ''
                }`}>
                  {day.date.getDate()}
                </div>

                <div className="flex-1 overflow-y-auto text-xs space-y-1 mt-1">
                  {dayEvents.slice(0, 3).map(event => (
                    <div
                      key={event.id}
                      className="px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: event.color }}
                      onClick={() => setSelectedEvent(event)}
                    >
                      {event.isAllDay ? '● ' : `${new Date(event.startDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - `}
                      {event.title}
                    </div>
                  ))}

                  {dayEvents.length > 3 && (
                    <div className="px-1 py-0.5 text-gray-400 cursor-pointer hover:text-gray-300">
                      + {dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAgendaView = () => (
    <div className="h-full">Agenda view placeholder</div>
  );

  return (
    <div className="bg-secondary text-white min-h-screen flex flex-col">
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-1/2 -left-1/4 w-3/4 h-3/4 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-3/4 h-3/4 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/3 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <header className="bg-primary/80 backdrop-blur-md px-6 py-4 border-b border-border-color sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold text-white">Calendar</h1>
          <button
            onClick={() => navigate('/chat')}
            className="text-sm px-4 py-2 rounded-md bg-accent hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
          >
            Back to Chat
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 flex-1 overflow-hidden flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
          </div>
        ) : (
          <div className="bg-primary/90 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/5 relative flex-1 flex flex-col">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-accent/5 opacity-50"></div>
            <div className="p-8 relative flex flex-col h-full">
              <div className="mb-6 pb-6 border-b border-border-color flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-extrabold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                    Calendar
                  </h2>
                  <p className="text-gray-400">Manage and organize your events and meetings</p>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={() => setIsAddingEvent(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors text-white text-sm font-medium shadow-lg shadow-accent/20"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Event
                  </button>
                  <button
                    onClick={() => setShowSidebar(!showSidebar)}
                    className="bg-tertiary px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors text-sm"
                  >
                    {showSidebar ? 'Hide Sidebar' : 'Show Sidebar'}
                  </button>
                </div>
              </div>

              {/* Calendar Controls */}
              <div className="bg-tertiary rounded-xl p-4 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={goToToday}
                    className="bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    Today
                  </button>
                  <div className="flex">
                    <button
                      onClick={goToPrevious}
                      className="bg-gray-700 hover:bg-gray-600 px-2 py-1.5 rounded-l-lg transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={goToNext}
                      className="bg-gray-700 hover:bg-gray-600 px-2 py-1.5 rounded-r-lg transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  <h2 className="text-lg font-semibold ml-2">{getViewTitle()}</h2>
                </div>

                <div className="flex bg-gray-700 rounded-lg overflow-hidden">
                  {(['day', 'week', 'month', 'agenda'] as CalendarView[]).map((v) => (
                    <button
                      key={v}
                      onClick={() => setView(v)}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                        view === v ? 'bg-accent text-white' : 'hover:bg-gray-600'
                      }`}
                    >
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calendar Main Content */}
              <div className="flex flex-1 gap-4 overflow-hidden min-h-0">
                {/* Main Calendar */}
                <div className="flex-1 bg-tertiary rounded-xl overflow-auto">
                  {view === 'day' && renderDayView()}
                  {view === 'week' && renderWeekView()}
                  {view === 'month' && renderMonthView()}
                  {view === 'agenda' && renderAgendaView()}
                </div>

                {/* Sidebar */}
                {showSidebar && (
                  <div className="w-80 bg-tertiary rounded-xl p-4 overflow-y-auto">
                    <h3 className="font-medium mb-3">Upcoming Events</h3>
                    <div className="space-y-2">
                      {events.slice(0, 5).map(event => (
                        <div
                          key={event.id}
                          className="p-3 rounded-lg bg-gray-800 hover:bg-gray-700 cursor-pointer transition-colors"
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: event.color }}></div>
                            <p className="font-medium truncate">{event.title}</p>
                          </div>
                          <p className="text-sm text-gray-400 mt-1">
                            {new Date(event.startDate).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 border-t border-gray-700 pt-4">
                      <h3 className="font-medium mb-3">Connected Services</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">G</div>
                            <span>Google Calendar</span>
                          </div>
                          <button className="text-sm px-2 py-1 rounded bg-blue-500/20 text-blue-400">
                            Connected
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white">O</div>
                            <span>Outlook</span>
                          </div>
                          <button className="text-sm px-2 py-1 rounded bg-gray-700 text-gray-300">
                            Connect
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-tertiary rounded-xl w-full max-w-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold">{selectedEvent.title}</h3>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-300">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>

                    <div>
                      {new Date(selectedEvent.startDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}

                      {!selectedEvent.isAllDay && (
                        <span>
                          , {new Date(selectedEvent.startDate).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })} - {new Date(selectedEvent.endDate).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      )}

                      {selectedEvent.isAllDay && <span className="ml-2 text-sm bg-gray-700 rounded px-2 py-0.5">All day</span>}
                      {selectedEvent.isRecurring && (
                        <span className="ml-2 text-sm bg-gray-700 rounded px-2 py-0.5">Recurring ({selectedEvent.recurringPattern})</span>
                      )}
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}

                  {selectedEvent.description && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                      <p className="text-gray-200 whitespace-pre-line">{selectedEvent.description}</p>
                    </div>
                  )}

                  {selectedEvent.participants.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Participants</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                            {selectedEvent.organizer.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div>
                            <p className="font-medium">{selectedEvent.organizer.name} <span className="text-xs bg-gray-700 rounded px-1">Organizer</span></p>
                            <p className="text-xs text-gray-400">{selectedEvent.organizer.email}</p>
                          </div>
                        </div>

                        {selectedEvent.participants.map(participant => (
                          <div key={participant.id} className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-sm font-medium">
                              {participant.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <div>
                              <p className="font-medium">{participant.name}</p>
                              <p className="text-xs text-gray-400">{participant.email}</p>
                            </div>
                            <div className="ml-auto">
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                participant.responseStatus === 'accepted' ? 'bg-green-500/20 text-green-400' :
                                participant.responseStatus === 'declined' ? 'bg-red-500/20 text-red-400' :
                                participant.responseStatus === 'tentative' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {participant.responseStatus === 'accepted' ? 'Accepted' :
                                 participant.responseStatus === 'declined' ? 'Declined' :
                                 participant.responseStatus === 'tentative' ? 'Maybe' :
                                 'No response'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedEvent.reminders.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Reminders</h4>
                      <div className="space-y-1">
                        {selectedEvent.reminders.map(reminder => (
                          <div key={reminder.id} className="flex items-center gap-2 text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            <span>
                              {reminder.time} {reminder.time === 1 ? 'minute' : 'minutes'} before ({reminder.type})
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex border-t border-gray-700 text-sm">
                <button className="flex-1 py-3 text-center hover:bg-gray-700 transition-colors">
                  Edit
                </button>
                <button className="flex-1 py-3 text-center hover:bg-gray-700 transition-colors text-red-400">
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Event Modal */}
      <AnimatePresence>
        {isAddingEvent && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-tertiary rounded-xl w-full max-w-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h3 className="text-2xl font-bold">Add New Event</h3>
                  <button
                    onClick={() => setIsAddingEvent(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Event Title</label>
                    <input
                      type="text"
                      placeholder="Add title"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Start Date</label>
                      <input
                        type="date"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">End Date</label>
                      <input
                        type="date"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Start Time</label>
                      <input
                        type="time"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">End Time</label>
                      <input
                        type="time"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allDay"
                      className="w-4 h-4 text-accent bg-gray-700 border-gray-600 rounded focus:ring-accent focus:ring-offset-gray-800"
                    />
                    <label htmlFor="allDay" className="ml-2 text-sm font-medium text-gray-300">All day</label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Location</label>
                    <input
                      type="text"
                      placeholder="Add location"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                    <textarea
                      placeholder="Add description"
                      rows={3}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Color</label>
                    <div className="flex gap-2">
                      {['#4CAF50', '#2196F3', '#F44336', '#9C27B0', '#FF9800', '#795548', '#607D8B'].map(color => (
                        <button
                          key={color}
                          type="button"
                          className="w-8 h-8 rounded-full border-2 border-gray-700 flex items-center justify-center"
                          style={{ backgroundColor: color }}
                        >
                          {color === '#2196F3' && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Participants</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <div className="flex items-center bg-gray-700 rounded-full pl-1 pr-2 py-1">
                        <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium mr-1">
                          AS
                        </div>
                        <span className="text-sm">Alex Smith</span>
                        <button className="ml-1 text-gray-400 hover:text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      <button className="flex items-center bg-gray-700 hover:bg-gray-600 rounded-full px-3 py-1 text-sm transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Reminders</label>
                    <div className="flex items-center gap-2">
                      <select className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent">
                        <option>15 minutes before</option>
                        <option>30 minutes before</option>
                        <option>1 hour before</option>
                        <option>1 day before</option>
                      </select>

                      <button type="button" className="bg-gray-700 hover:bg-gray-600 p-2 rounded-lg transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="flex border-t border-gray-700">
                <button
                  className="flex-1 py-3 text-center hover:bg-gray-700 transition-colors"
                  onClick={() => setIsAddingEvent(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 text-center bg-accent hover:bg-accent/90 transition-opacity font-medium"
                  onClick={() => {
                    // Normally would save the event data
                    setIsAddingEvent(false);
                    // Show a success notification or something
                  }}
                >
                  Save Event
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Dummy data for events
const dummyEvents: CalendarEvent[] = [
  {
    id: 'event-1',
    title: 'Weekly Team Meeting',
    description: 'Discuss project progress and upcoming deadlines.',
    startDate: '2023-06-05T10:00:00.000Z',
    endDate: '2023-06-05T11:00:00.000Z',
    location: 'Conference Room A',
    color: '#4CAF50',
    isAllDay: false,
    isRecurring: true,
    recurringPattern: 'weekly',
    organizer: {
      id: '1',
      name: 'Alex Smith',
      email: 'alex@example.com',
      responseStatus: 'accepted'
    },
    participants: [
      {
        id: '2',
        name: 'Jamie Taylor',
        email: 'jamie@example.com',
        responseStatus: 'accepted'
      },
      {
        id: '3',
        name: 'Jordan Williams',
        email: 'jordan@example.com',
        responseStatus: 'tentative'
      }
    ],
    reminders: [
      {
        id: 'r1',
        time: 15,
        type: 'notification'
      }
    ]
  },
  {
    id: 'event-2',
    title: 'Product Demo',
    description: 'Showcase the new features to the client.',
    startDate: '2023-06-06T14:00:00.000Z',
    endDate: '2023-06-06T15:30:00.000Z',
    location: 'Zoom Meeting',
    color: '#2196F3',
    isAllDay: false,
    isRecurring: false,
    organizer: {
      id: '1',
      name: 'Alex Smith',
      email: 'alex@example.com',
      responseStatus: 'accepted'
    },
    participants: [
      {
        id: '4',
        name: 'Casey Brown',
        email: 'casey@example.com',
        responseStatus: 'accepted'
      }
    ],
    reminders: [
      {
        id: 'r2',
        time: 30,
        type: 'notification'
      },
      {
        id: 'r3',
        time: 60,
        type: 'email'
      }
    ]
  },
  {
    id: 'event-3',
    title: 'Project Deadline',
    description: 'UI/UX design completion deadline',
    startDate: '2023-06-07T00:00:00.000Z',
    endDate: '2023-06-07T23:59:59.000Z',
    color: '#F44336',
    isAllDay: true,
    isRecurring: false,
    organizer: {
      id: '3',
      name: 'Jordan Williams',
      email: 'jordan@example.com',
      responseStatus: 'accepted'
    },
    participants: [],
    reminders: [
      {
        id: 'r4',
        time: 1440, // 1 day
        type: 'notification'
      }
    ],
    taskId: 'task-1'
  },
  {
    id: 'event-4',
    title: 'Client Meeting',
    description: 'Discuss requirements for the new project',
    startDate: '2023-06-08T11:00:00.000Z',
    endDate: '2023-06-08T12:00:00.000Z',
    location: 'Conference Room B',
    color: '#9C27B0',
    isAllDay: false,
    isRecurring: false,
    organizer: {
      id: '1',
      name: 'Alex Smith',
      email: 'alex@example.com',
      responseStatus: 'accepted'
    },
    participants: [
      {
        id: '5',
        name: 'Morgan Lee',
        email: 'morgan@example.com',
        responseStatus: 'needsAction'
      }
    ],
    reminders: [
      {
        id: 'r5',
        time: 15,
        type: 'notification'
      }
    ]
  },
  {
    id: 'event-5',
    title: 'Training Session',
    description: 'New tool training for the team',
    startDate: '2023-06-09T15:00:00.000Z',
    endDate: '2023-06-09T17:00:00.000Z',
    location: 'Training Room',
    color: '#FF9800',
    isAllDay: false,
    isRecurring: false,
    organizer: {
      id: '4',
      name: 'Casey Brown',
      email: 'casey@example.com',
      responseStatus: 'accepted'
    },
    participants: [
      {
        id: '1',
        name: 'Alex Smith',
        email: 'alex@example.com',
        responseStatus: 'accepted'
      },
      {
        id: '2',
        name: 'Jamie Taylor',
        email: 'jamie@example.com',
        responseStatus: 'declined'
      },
      {
        id: '3',
        name: 'Jordan Williams',
        email: 'jordan@example.com',
        responseStatus: 'accepted'
      }
    ],
    reminders: [
      {
        id: 'r6',
        time: 60,
        type: 'notification'
      }
    ]
  }
];

export default Calendar;
